import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import { blogService, AnalyticsOverview } from '../../services/blogService';
import { generateFluxSnippet } from '../../utils/fluxSnippet';
import { Activity, ExternalLink, Globe, Users, TrendingUp, MousePointerClick, MapPin, BarChart3, Copy, Zap, Eye, ArrowUpRight, KeyRound, Save } from 'lucide-react';

const BlogAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  // Manual Flux credentials (used to generate the snippet before a connection has Flux configured)
  const [appIdInput, setAppIdInput] = useState<string>('');
  const [apiKeyInput, setApiKeyInput] = useState<string>('');
  const [savingFlux, setSavingFlux] = useState<boolean>(false);

  const FEATURES = [
    { icon: Eye, title: t('blogSeo.analytics.featureRealtimeTitle', 'Acessos em tempo real'), desc: t('blogSeo.analytics.featureRealtimeDesc', 'Veja quem está navegando no seu blog neste exato momento.') },
    { icon: MapPin, title: t('blogSeo.analytics.featureSourcesTitle', 'Origens de tráfego'), desc: t('blogSeo.analytics.featureSourcesDesc', 'Descubra de onde vêm seus visitantes: busca, social ou direto.') },
    { icon: BarChart3, title: t('blogSeo.analytics.featureTopPagesTitle', 'Páginas mais vistas'), desc: t('blogSeo.analytics.featureTopPagesDesc', 'Identifique os conteúdos que mais atraem leitores.') },
    { icon: Users, title: t('blogSeo.analytics.featureSessionsTitle', 'Sessões e visitantes'), desc: t('blogSeo.analytics.featureSessionsDesc', 'Acompanhe o alcance real do seu blog ao longo do tempo.') },
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await blogService.getAnalytics();
        if (mounted) setAnalytics(res?.data ?? null);
      } catch (err) {
        if (mounted) toast.error(t('blogSeo.analytics.loadError', 'Não foi possível carregar os dados de acessos.'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flux = analytics?.flux;
  const posts = analytics?.posts;
  const isConnected = flux?.enabled === true;

  const copySnippet = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('blogSeo.analytics.snippetCopied', 'Snippet copiado!'));
    } catch (err) {
      toast.error(t('blogSeo.analytics.snippetCopyError', 'Não foi possível copiar o snippet.'));
    }
  };

  // Persist the manually-entered Flux credentials onto the default blog connection,
  // which flips this tab into the "connected" state.
  const handleSaveFlux = async () => {
    const appId = appIdInput.trim();
    const apiKey = apiKeyInput.trim();
    if (!appId || !apiKey) {
      toast.error(t('blogSeo.analytics.fluxFillBoth', 'Preencha o App ID e a API Key.'));
      return;
    }
    try {
      setSavingFlux(true);
      const list = await blogService.listConnections();
      const conns = list?.data || [];
      const target = conns.find((c) => c.is_default) || conns[0];
      if (!target) {
        toast.error(t('blogSeo.analytics.fluxNoConnection', 'Crie uma conexão de blog na aba Conexões para salvar o Flux.'));
        return;
      }
      await blogService.updateConnection(target.id, { flux_enabled: true, flux_app_id: appId, flux_api_key: apiKey });
      toast.success(t('blogSeo.analytics.fluxSaved', 'Flux ativado!'));
      const res = await blogService.getAnalytics();
      setAnalytics(res?.data ?? null);
    } catch (err) {
      toast.error(t('blogSeo.analytics.fluxSaveError', 'Não foi possível salvar o Flux.'));
    } finally {
      setSavingFlux(false);
    }
  };

  const manualSnippet = generateFluxSnippet(apiKeyInput.trim(), appIdInput.trim());
  const manualReady = !!appIdInput.trim() && !!apiKeyInput.trim();

  const InstallCard: React.FC<{ secondary?: boolean }> = ({ secondary }) => {
    const { t } = useTranslation();
    if (!flux) return null;
    return (
      <Card className={secondary ? 'opacity-90' : ''}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('blogSeo.analytics.installTitle', 'Como instalar')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('blogSeo.analytics.installSubtitlePrefix', 'Cole o snippet do DevSkin Browser SDK dentro da tag')} <span className="font-mono text-gray-700 dark:text-gray-300">&lt;head&gt;</span> {t('blogSeo.analytics.installSubtitleSuffix', 'do seu blog.')}
              </p>
            </div>
          </div>
          <Button variant="secondary" size="sm" leftIcon={<Copy className="w-4 h-4" />} onClick={() => copySnippet(flux.installSnippet)}>
            {t('blogSeo.analytics.copy', 'Copiar')}
          </Button>
        </div>
        <pre className="bg-gray-900 dark:bg-black/60 text-gray-100 rounded-lg p-4 font-mono text-sm overflow-x-auto whitespace-pre-wrap border border-gray-800">
          <code>{flux.installSnippet}</code>
        </pre>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          {t('blogSeo.analytics.installNote', 'O Flux é totalmente client-side: depois de instalado, ele envia automaticamente acessos, origens, páginas mais vistas e sessões para o devskin-monitoring. Não há nenhum passo de ativação via API.')}
        </p>
        {flux.hint && (
          <p className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">{flux.hint}</p>
        )}
      </Card>
    );
  };

  const FeaturesCard: React.FC = () => {
    const { t } = useTranslation();
    return (
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400">
            <Globe className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('blogSeo.analytics.featuresTitle', 'O que o Flux monitora')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('blogSeo.analytics.featuresSubtitle', 'Métricas completas de tráfego diretamente do devskin-monitoring.')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/50"
              >
                <div className="flex items-center justify-center w-9 h-9 shrink-0 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-indigo-600 dark:text-indigo-400">
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{f.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 border-t-indigo-600 dark:border-t-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('blogSeo.analytics.headerTitle', 'Acessos & Tráfego')}</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('blogSeo.analytics.headerSubtitle', 'Monitore acessos, páginas mais vistas e origens de tráfego com o Flux (devskin-monitoring).')}
        </p>
      </div>

      {/* CASE A — NOT connected */}
      {!isConnected && (
        <>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 p-8 text-white shadow-lg">
            <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
            <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 backdrop-blur">
                  <Activity className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{t('blogSeo.analytics.notConnectedTitle', 'Instale o Flux para ver seus acessos')}</h3>
                  <p className="mt-1 max-w-xl text-sm text-indigo-100">
                    {t('blogSeo.analytics.notConnectedDescPrefix', 'Cole o snippet abaixo no')} <span className="font-semibold">&lt;head&gt;</span> {t('blogSeo.analytics.notConnectedDescSuffix', 'do seu blog. A partir daí, o Flux passa a enviar acessos, origens, páginas mais vistas e sessões automaticamente.')}
                  </p>
                </div>
              </div>
              {flux?.dashboardUrl && (
                <div className="flex shrink-0 flex-col gap-2">
                  <a href={flux.dashboardUrl} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="secondary"
                      leftIcon={<ExternalLink className="w-4 h-4" />}
                      className="w-full !text-indigo-700"
                    >
                      {t('blogSeo.analytics.openMonitoring', 'Abrir devskin-monitoring')}
                    </Button>
                  </a>
                  <p className="text-xs text-indigo-100">
                    {t('blogSeo.analytics.realNumbersInPanel', 'Os números reais ficam no painel do Flux.')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Step 1: ask for the Flux credentials, THEN generate the real snippet */}
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('blogSeo.analytics.credentialsTitle', 'Conecte seu Flux')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('blogSeo.analytics.credentialsSubtitle', 'Informe o App ID e a API Key do seu app no devskin-monitoring para gerar o snippet.')}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('blogSeo.analytics.appIdLabel', 'App ID')}
                value={appIdInput}
                onChange={(e) => setAppIdInput(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="font-mono"
              />
              <Input
                label={t('blogSeo.analytics.apiKeyLabel', 'API Key')}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="dsk_pub_..."
                className="font-mono"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('blogSeo.analytics.credentialsHint', 'Encontre essas chaves em flux.devskin.com → Configurações do seu app.')}
            </p>

            {!manualReady ? (
              <div className="mt-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/60 dark:bg-gray-800/40 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('blogSeo.analytics.snippetLocked', 'Preencha o App ID e a API Key acima para gerar o snippet de instalação.')}
              </div>
            ) : (
              <>
                <div className="mt-5 flex items-center justify-between gap-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('blogSeo.analytics.snippetReady', 'Seu snippet (cole no')} <span className="font-mono">&lt;head&gt;</span> {t('blogSeo.analytics.snippetReadySuffix', 'do blog):')}
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="secondary" size="sm" leftIcon={<Copy className="w-4 h-4" />} onClick={() => copySnippet(manualSnippet)}>
                      {t('blogSeo.analytics.copy', 'Copiar')}
                    </Button>
                    <Button variant="primary" size="sm" leftIcon={<Save className="w-4 h-4" />} onClick={handleSaveFlux} isLoading={savingFlux}>
                      {t('blogSeo.analytics.fluxSave', 'Salvar e ativar')}
                    </Button>
                  </div>
                </div>
                <pre className="mt-3 bg-gray-900 dark:bg-black/60 text-gray-100 rounded-lg p-4 font-mono text-sm overflow-x-auto whitespace-pre-wrap border border-gray-800">
                  <code>{manualSnippet}</code>
                </pre>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  {t('blogSeo.analytics.installNote', 'O Flux é totalmente client-side: depois de instalado, ele envia automaticamente acessos, origens, páginas mais vistas e sessões para o devskin-monitoring. Não há nenhum passo de ativação via API.')}
                </p>
              </>
            )}
          </Card>
          <FeaturesCard />
        </>
      )}

      {/* CASE B — connected */}
      {isConnected && (
        <>
          <div className="rounded-2xl border border-green-200 dark:border-green-500/30 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/10 dark:to-emerald-500/10 p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-green-500/20 text-green-600 dark:text-green-400">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('blogSeo.analytics.fluxActive', 'Flux ativo')}</h3>
                    <Badge variant="success">{t('blogSeo.analytics.connected', 'Conectado')}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {flux?.appId && (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2 py-1 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {t('blogSeo.analytics.appLabel', 'App:')} {flux.appId}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {flux?.dashboardUrl && (
                <a href={flux.dashboardUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <Button variant="primary" rightIcon={<ArrowUpRight className="w-4 h-4" />} className="w-full">
                    {t('blogSeo.analytics.openFluxPanel', 'Abrir painel do Flux')}
                  </Button>
                </a>
              )}
            </div>
          </div>

          {/* KPI placeholder cards — números reais ficam no painel do Flux */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Eye, label: t('blogSeo.analytics.kpiVisits', 'Acessos') },
              { icon: Users, label: t('blogSeo.analytics.kpiUniqueVisitors', 'Visitantes únicos') },
              { icon: MapPin, label: t('blogSeo.analytics.kpiSources', 'Origens') },
              { icon: MousePointerClick, label: t('blogSeo.analytics.kpiClickRate', 'Taxa de cliques') },
            ].map((kpi) => {
              const Icon = kpi.icon;
              const content = (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                      <Icon className="w-5 h-5" />
                    </div>
                    <TrendingUp className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-indigo-400" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-gray-900 dark:text-white">{kpi.label}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('blogSeo.analytics.seeInPanel', 'Veja no painel')} <ArrowUpRight className="w-3 h-3" />
                  </p>
                </>
              );
              const cardClass =
                'group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-5 transition-all hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:shadow-md';
              return flux?.dashboardUrl ? (
                <a
                  key={kpi.label}
                  href={flux.dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClass}
                >
                  {content}
                </a>
              ) : (
                <div key={kpi.label} className={cardClass}>
                  {content}
                </div>
              );
            })}
          </div>

          <FeaturesCard />
          <InstallCard secondary />
        </>
      )}

      {/* BOTH — content summary */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('blogSeo.analytics.contentSummaryTitle', 'Resumo do conteúdo')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('blogSeo.analytics.contentSummarySubtitle', 'Estatísticas dos posts do seu blog.')}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: t('blogSeo.analytics.summaryTotal', 'Total'), value: posts?.total ?? 0 },
            { label: t('blogSeo.analytics.summaryPublished', 'Publicados'), value: posts?.published ?? 0 },
            { label: t('blogSeo.analytics.summaryScheduled', 'Agendados'), value: posts?.scheduled ?? 0 },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/50 p-4 text-center"
            >
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BlogAnalytics;
